import {
  type InstalledPlugin,
  isListedInPlugins,
  isUserPluginSource,
  type PluginCatalogItem,
  type PluginKind,
} from '@crowdex/plugin-contracts';

/** Whether the plugin uses accounts: connections of the hub, of its services or the ones it requires. */
export function isPluginConnectionConfigurable(plugin: InstalledPlugin): boolean {
  return pluginAccountServices(plugin).size > 0;
}

/** Whether the plugin has preferences; its accounts are connections of the hub. */
export function isPluginConfigurable(plugin: InstalledPlugin): boolean {
  return Object.keys(plugin.settings ?? {}).length > 0;
}

/** Source writes are restricted to explicit local plugins and workspace discoveries. */
export function isPluginSourceConfigurable(plugin: InstalledPlugin): boolean {
  return isUserPluginSource(plugin.source) && (plugin.sourceFiles?.length ?? 0) > 0;
}

/**
 * Whether an installed plugin is connected: the hub has an account of one of its services
 * (`accounts`, the hub's accounts by connection type).
 */
export function isPluginConnected(
  plugin: InstalledPlugin,
  accounts: readonly string[] = [],
): boolean {
  return [...pluginAccountServices(plugin)].some((service) => accounts.includes(service));
}

/** The services whose hub accounts a plugin uses: its own, its connectors' and the ones it requires. */
export function pluginAccountServices(plugin: InstalledPlugin): Set<string> {
  return new Set([
    ...(plugin.service === null ? [] : [plugin.service]),
    ...(plugin.connectors ?? []).map((connector) => connector.service),
    ...(plugin.requires?.connections ?? []).map((connection) => connection.service),
  ]);
}

/**
 * What a plugin is: the kind derived from its `crowdex.json` (`pluginKind`), else from what it
 * contributes. Coding agents are wiring wherever they come from.
 */
export function installedPluginKind(plugin: PluginCatalogItem | InstalledPlugin): PluginKind {
  const derived = 'crowdex' in plugin ? plugin.crowdex?.kind : undefined;
  if (derived !== undefined) return derived;
  if (plugin.contributes.length > 0 && plugin.contributes.every((kind) => kind === 'agents'))
    return 'agent';
  return 'plugin';
}

/** Whether Settings › Plugins and ⌘K list it: providers and coding agents are hidden wiring. */
export function isShownInPlugins(plugin: PluginCatalogItem | InstalledPlugin): boolean {
  return isListedInPlugins({ kind: installedPluginKind(plugin) });
}
